import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { ApiError } from "../api/client";
import { resetPassword } from "../api/auth";
import { AuthShell } from "../components/AuthShell";
import { Button, Field } from "../components/Page";

/**
 * Set a new password from the link in the reset mail.
 *
 * The API caps the password at 72 characters, which is bcrypt's own limit —
 * anything longer is silently truncated by the hash, so the inputs enforce the
 * same ceiling rather than letting a user believe a longer secret was stored.
 */
export default function ResetPasswordPage() {
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tooLong = password.length > 72;
  const mismatch = confirm.length > 0 && confirm !== password;
  const tooShort = password.length > 0 && password.length < 8;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (tooLong || mismatch || tooShort) return;
    setBusy(true);
    setError(null);
    try {
      await resetPassword(token ?? "", password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not reset your password.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title="Choose a new password"
      lede="Pick something you have not used here before."
      footer={
        done ? (
          <Link to="/login" className="font-medium text-primary underline">
            Go to sign in
          </Link>
        ) : null
      }
    >
      {done ? (
        <p className="text-sm leading-relaxed text-ink-muted">
          Your password has been changed. Sign in with it now.
        </p>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <Field
            id="password"
            name="password"
            type="password"
            label="New password"
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={72}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Field
            id="confirm"
            name="confirm"
            type="password"
            label="Confirm new password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />

          {tooShort ? <p className="text-sm text-danger">Use at least 8 characters.</p> : null}
          {tooLong ? (
            <p className="text-sm text-danger">
              Use 72 characters or fewer — anything longer is cut off by the encryption.
            </p>
          ) : null}
          {mismatch ? <p className="text-sm text-danger">The two passwords do not match.</p> : null}
          {error ? (
            <p role="alert" className="rounded-r border-l-[3px] border-danger bg-danger-wash px-3.5 py-2.5 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <Button type="submit" disabled={busy || tooLong || mismatch || tooShort} className="w-full">
            {busy ? "Saving…" : "Change password"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}